import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import CompanyPermissionsTab from '../components/CompanyPermissionsTab';
import CompanyModulesTab from '../components/CompanyModulesTab';
import CompanyActivityTab from '../components/CompanyActivityTab';
import { MODULES_BY_CODE, formatPrice } from '../modules/catalog';
import useSortPage from '../hooks/useSortPage';
import TablePagination from '../components/TablePagination';
import SortableTh from '../components/SortableTh';

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    contactEmail: '',
    logoUrl: '',
    financialYearStart: 1,
    financialYearStartDay: 1,
    financialYearEnd: 12,
    financialYearEndDay: 31
  });
  
  const [ssoData, setSsoData] = useState({
    azureAdTenantId: '',
    azureAdClientId: '',
    azureAdClientSecret: '',
    allowedDomains: [],
    autoProvisionEnabled: true,
    requireAdminApproval: false,
    isActive: true
  });
  const [newDomain, setNewDomain] = useState('');

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [companyModules, setCompanyModules] = useState(null);
  const [companyModulePrices, setCompanyModulePrices] = useState(null);
  const [companyModulePriceOverrides, setCompanyModulePriceOverrides] = useState({});
  const ssoGuideSlug = 'azure-ad-sso-customer-checklist';
  const ssoGuidePath = `/help/${ssoGuideSlug}`;
  const ssoGuideUrl = `${window.location.origin}${ssoGuidePath}`;
  const [stagedSsoUser, setStagedSsoUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roleIds: []
  });
  const [stagedSsoInitialized, setStagedSsoInitialized] = useState(false);

  const openSsoPreprovision = () => {
    setActiveTab('sso');
    setTimeout(() => {
      const el = document.getElementById('sso-preprovision-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  useEffect(() => {
    loadCompany();
    loadUsers();
    loadRoles();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'usage' && !usageData) {
      loadUsageMetrics();
    }
  }, [activeTab]);

  useEffect(() => {
    if (stagedSsoInitialized || roles.length === 0) {
      return;
    }

    const defaultRoleNames = ['Admin', 'User'];
    const defaultRoleIds = roles
      .filter(role => defaultRoleNames.includes(role.RoleName))
      .map(role => role.RoleID);

    setStagedSsoUser(prev => ({
      ...prev,
      roleIds: prev.roleIds.length > 0 ? prev.roleIds : defaultRoleIds
    }));
    setStagedSsoInitialized(true);
  }, [roles, stagedSsoInitialized]);

  const loadUsageMetrics = async () => {
    try {
      setUsageLoading(true);
      const response = await api.get(`/companies/${id}/usage?months=6`);
      setUsageData(response.data);
      try {
        const modRes = await api.get(`/companies/${id}/modules`);
        setCompanyModules(modRes.data?.enabled || []);
        setCompanyModulePrices(modRes.data?.prices || {});
        setCompanyModulePriceOverrides(modRes.data?.priceOverrides || {});
      } catch (modErr) {
        console.warn('Failed to load modules for pricing breakdown:', modErr);
        setCompanyModules([]);
        setCompanyModulePrices({});
        setCompanyModulePriceOverrides({});
      }
    } catch (err) {
      console.error('Failed to load usage metrics:', err);
    } finally {
      setUsageLoading(false);
    }
  };

  const loadCompany = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/companies/${id}`);
      // Backend returns { company: {...}, stats: {...} }
      const companyData = response.data.company || response.data;
      setCompany(companyData);
      setFormData({
        companyName: companyData.CompanyName || '',
        address: companyData.Address || '',
        contactEmail: companyData.ContactEmail || '',
        logoUrl: companyData.LogoUrl || '',
        financialYearStart: companyData.FinancialYearStart || 1,
        financialYearStartDay: companyData.FinancialYearStartDay || 1,
        financialYearEnd: companyData.FinancialYearEnd || 12,
        financialYearEndDay: companyData.FinancialYearEndDay || 31
      });

      // Always try to load SSO settings (will return null if not configured)
      loadSsoSettings();
    } catch (err) {
      console.error('Failed to load company:', err);
      setError(err.response?.data?.message || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const loadSsoSettings = async () => {
    try {
      const response = await api.get(`/settings/company-sso/${id}`);
      if (response.data) {
        let domains = [];
        if (response.data.AllowedDomains) {
          try {
            domains = JSON.parse(response.data.AllowedDomains);
          } catch (e) {
            console.error('Failed to parse allowed domains:', e);
          }
        }
        
        setSsoData({
          azureAdTenantId: response.data.AzureAdTenantId || '',
          azureAdClientId: response.data.AzureAdClientId || '',
          azureAdClientSecret: '', // Don't load secret
          allowedDomains: domains,
          autoProvisionEnabled: response.data.AutoProvisionEnabled ?? true,
          requireAdminApproval: response.data.RequireAdminApproval ?? false,
          isActive: response.data.IsActive ?? true
        });
      }
    } catch (err) {
      console.error('Failed to load SSO settings:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get(`/users?companyId=${id}`);
      setUsers(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get(`/roles?companyId=${id}`);
      setRoles(response.data.roles || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const handleToggleUserStatus = async (userId, isCurrentlyActive) => {
    if (!confirm(`Are you sure you want to ${isCurrentlyActive ? 'deactivate' : 'activate'} this user?`)) {
      return;
    }
    
    try {
      await api.put(`/users/${userId}?companyId=${id}`, { isActive: !isCurrentlyActive });
      alert('User status updated successfully');
      loadUsers();
    } catch (err) {
      console.error('Failed to update user status:', err);
      alert(err.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!confirm(`Are you sure you want to delete ${username}? They can be re-provisioned if they login again via SSO.`)) {
      return;
    }
    
    try {
      await api.delete(`/users/${userId}?companyId=${id}`);
      alert('User removed from company successfully');
      loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleChangeUserRole = (user) => {
    setSelectedUser(user);
    // Parse current role IDs from the user object
    const currentRoleIds = user.RoleIDs ? user.RoleIDs.split(',') : [];
    setSelectedRoleIds(currentRoleIds);
    setShowRoleModal(true);
  };

  const handleToggleRole = (roleId) => {
    setSelectedRoleIds(prev => {
      if (prev.includes(roleId)) {
        // Remove role (but ensure at least one role remains)
        const updated = prev.filter(id => id !== roleId);
        if (updated.length === 0) {
          alert('User must have at least one role');
          return prev;
        }
        return updated;
      } else {
        // Add role
        return [...prev, roleId];
      }
    });
  };

  const handleSaveUserRole = async () => {
    if (!selectedUser || !selectedRoleIds || selectedRoleIds.length === 0) {
      alert('Please select at least one role');
      return;
    }
    
    try {
      await api.post(`/users/${selectedUser.UserID}/roles`, {
        roleIds: selectedRoleIds,
        companyId: id
      });
      alert(`User roles updated successfully. Assigned ${selectedRoleIds.length} role(s).`);
      setShowRoleModal(false);
      setSelectedUser(null);
      setSelectedRoleIds([]);
      loadUsers();
    } catch (err) {
      console.error('Failed to update user roles:', err);
      alert(err.response?.data?.message || 'Failed to update user roles');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !userFilter ||
      user.Username?.toLowerCase().includes(userFilter.toLowerCase()) ||
      user.Email?.toLowerCase().includes(userFilter.toLowerCase()) ||
      user.FullName?.toLowerCase().includes(userFilter.toLowerCase());
    
    const matchesRole = !roleFilter || 
      (user.RoleNames && user.RoleNames.split(', ').includes(roleFilter));

    const matchesStatus = !statusFilter ||
      (statusFilter === 'active'
        ? user.IsActive === true || user.IsActive === 1
        : user.IsActive !== true && user.IsActive !== 1);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const { sortBy: userSortBy, sortDir: userSortDir, handleSort: handleUserSort, resetPage: resetUserPage, sortedData: userSortedData, paginatedData: userPagedData, paginationProps: userPaginationProps } =
    useSortPage({ defaultSortBy: 'FullName', pageSize: 25 });

  const sortedUsers = userSortedData(filteredUsers);
  const pagedUsers = userPagedData(sortedUsers);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSsoInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSsoData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put(`/companies/${id}`, formData);
      alert('Company updated successfully');
      loadCompany();
    } catch (err) {
      console.error('Failed to update company:', err);
      alert(err.response?.data?.message || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSso = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put(`/settings/company-sso/${id}`, {
        azureAdTenantId: ssoData.azureAdTenantId,
        azureAdClientId: ssoData.azureAdClientId,
        azureAdClientSecret: ssoData.azureAdClientSecret || undefined,
        allowedDomains: JSON.stringify(ssoData.allowedDomains),
        autoProvisionEnabled: ssoData.autoProvisionEnabled,
        requireAdminApproval: ssoData.requireAdminApproval,
        isActive: ssoData.isActive
      });
      alert('SSO settings updated successfully');
      loadCompany();
      loadSsoSettings();
    } catch (err) {
      console.error('Failed to update SSO settings:', err);
      alert(err.response?.data?.message || 'Failed to update SSO settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = () => {
    if (!newDomain || !newDomain.includes('.')) {
      alert('Please enter a valid domain (e.g., company.com)');
      return;
    }
    const domain = newDomain.toLowerCase().replace(/^@/, '').trim();
    if (ssoData.allowedDomains.includes(domain)) {
      alert('Domain already exists');
      return;
    }
    setSsoData(prev => ({
      ...prev,
      allowedDomains: [...prev.allowedDomains, domain]
    }));
    setNewDomain('');
  };

  const handleRemoveDomain = (domain) => {
    setSsoData(prev => ({
      ...prev,
      allowedDomains: prev.allowedDomains.filter(d => d !== domain)
    }));
  };

  const handleStagedSsoInputChange = (e) => {
    const { name, value } = e.target;
    setStagedSsoUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleStagedSsoRole = (roleId) => {
    setStagedSsoUser(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId]
    }));
  };

  const handleCreateStagedSsoUser = async () => {
    if (!stagedSsoUser.email || stagedSsoUser.roleIds.length === 0) {
      alert('Email and at least one role are required');
      return;
    }

    try {
      setSaving(true);
      const normalizedEmail = stagedSsoUser.email.trim().toLowerCase();
      const response = await api.post('/users', {
        companyId: id,
        username: normalizedEmail,
        email: normalizedEmail,
        firstName: stagedSsoUser.firstName,
        lastName: stagedSsoUser.lastName,
        authType: 'sso',
        status: 'Active',
        roleIds: stagedSsoUser.roleIds
      });

      const wasReprovisioned = response.status === 200;
      setStagedSsoUser({
        firstName: '',
        lastName: '',
        email: '',
        roleIds: roles
          .filter(role => ['Admin', 'User'].includes(role.RoleName))
          .map(role => role.RoleID)
      });
      await loadUsers();
      if (wasReprovisioned) {
        // Existing user re-provisioned — they have LastLoginAt so won't appear in staged list
        // Switch to Users tab so admin can see them
        setStatusFilter('');
        setActiveTab('users');
        alert(`${normalizedEmail} has been re-granted access. You can see them in the Users tab below.`);
      } else {
        alert('SSO user pre-created successfully. They can now sign in with Microsoft and land with the assigned access.');
      }
    } catch (err) {
      console.error('Failed to pre-create SSO user:', err);
      alert(err.response?.data?.message || 'Failed to pre-create SSO user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!confirm(`Are you sure you want to ${company.IsActive ? 'deactivate' : 'activate'} this company?`)) {
      return;
    }
    
    try {
      setSaving(true);
      await api.put(`/companies/${id}`, {
        ...formData,
        isActive: !company.IsActive
      });
      alert(`Company ${company.IsActive ? 'deactivated' : 'activated'} successfully`);
      loadCompany();
    } catch (err) {
      console.error('Failed to toggle company status:', err);
      alert(err.response?.data?.message || 'Failed to update company status');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      await api.delete(`/companies/${id}`);
      alert('Company deleted successfully');
      navigate('/companies');
    } catch (err) {
      console.error('Failed to delete company:', err);
      alert(err.response?.data?.message || 'Failed to delete company');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading company details...</div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'Company not found'}
        </div>
        <button
          onClick={() => navigate('/companies')}
          className="text-blue-600 hover:underline"
        >
          ← Back to Companies
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/companies')}
          className="text-blue-600 hover:underline mb-2 flex items-center gap-1"
        >
          <span>←</span> Back to Companies
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{company.CompanyName}</h1>
            <p className="text-gray-500 mt-1">{company.ContactEmail}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleToggleActive}
              disabled={saving}
              className={`btn ${company.IsActive ? 'btn-warning' : 'btn-success'}`}
            >
              {company.IsActive ? 'Deactivate' : 'Activate'}
            </button>
            <button
              onClick={handleDelete}
              disabled={saving}
              className="btn btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-6">
        <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
          company.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {company.IsActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Company Details
          </button>
          <button
            onClick={() => setActiveTab('sso')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sso'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Azure AD SSO
            {company.SsoConfigured && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                Configured
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'usage'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 Usage & Billing
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'permissions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🔐 Permissions
          </button>
          <button
            onClick={() => setActiveTab('modules')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'modules'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ✨ Modules
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'activity'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📜 Activity
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'details' && (
          <form onSubmit={handleUpdateCompany} className="p-6 space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-emerald-900 mb-1">🚀 Company Onboarding (Recommended)</h3>
              <p className="text-xs text-emerald-800 mb-3">
                After creating the company, configure Azure AD SSO and pre-create the first company admin before they sign in.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openSsoPreprovision}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Go to SSO + Pre-provision Admin
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo URL
              </label>
              <input
                type="url"
                name="logoUrl"
                value={formData.logoUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email *
              </label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Financial Year</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Month
                  </label>
                  <select
                    name="financialYearStart"
                    value={formData.financialYearStart}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'long' })}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Day
                  </label>
                  <input
                    type="number"
                    name="financialYearStartDay"
                    value={formData.financialYearStartDay}
                    onChange={handleInputChange}
                    min="1"
                    max="31"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Month
                  </label>
                  <select
                    name="financialYearEnd"
                    value={formData.financialYearEnd}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'long' })}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Day
                  </label>
                  <input
                    type="number"
                    name="financialYearEndDay"
                    value={formData.financialYearEndDay}
                    onChange={handleInputChange}
                    min="1"
                    max="31"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/companies')}
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'sso' && (
          <form onSubmit={handleUpdateSso} className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-medium text-blue-900 mb-2">🔐 Azure AD Single Sign-On</h3>
              <p className="text-sm text-blue-700 mb-2">
                Configure Azure AD authentication for this company. Users from your Azure AD tenant will be able to sign in and auto-provision into the system.
              </p>
              <div className="bg-blue-100 border border-blue-300 rounded p-3 mt-3">
                <p className="text-xs font-medium text-blue-900 mb-1">📋 Auto-Provisioning Workflow:</p>
                <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
                  <li>User signs in with their company Azure AD credentials</li>
                  <li>System automatically creates user account with <strong>"User" role (minimum privileges)</strong></li>
                  <li>User status: {ssoData.requireAdminApproval ? <strong className="text-orange-700">"Pending Access" - requires admin approval</strong> : <strong className="text-green-700">"Active" - immediate access</strong>}</li>
                  <li>Admin assigns departments and upgrades role permissions as needed</li>
                  <li>Users can be assigned to multiple departments for cross-department work</li>
                </ol>
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-indigo-900">✅ Platform Admin Checklist (Send to Customer)</h3>
                  <p className="text-xs text-indigo-800 mt-1">
                    Share this guide link with the customer Azure admin before collecting Tenant ID, Client ID and Secret.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={ssoGuidePath}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    Open Guide
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(ssoGuideUrl);
                      alert('Guide link copied');
                    }}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  >
                    Copy Link
                  </button>
                </div>
              </div>

              <div className="bg-white border border-indigo-100 rounded p-3 mb-3">
                <p className="text-xs text-gray-700 break-all">
                  <span className="font-semibold">Customer Guide URL:</span> {ssoGuideUrl}
                </p>
              </div>

              <ul className="text-xs text-indigo-900 space-y-1 ml-4 list-disc">
                <li>Create a single-tenant App Registration in customer Azure AD</li>
                <li>Add redirect URI for your portal callback endpoint</li>
                <li>Create client secret and share the secret value once</li>
                <li>Grant Microsoft Graph delegated permissions: <strong>openid</strong>, <strong>profile</strong>, <strong>email</strong>, <strong>User.Read</strong></li>
                <li>For group sync, grant Graph application permissions: <strong>Group.Read.All</strong> and <strong>GroupMember.Read.All</strong></li>
                <li>Click <strong>Grant admin consent</strong> for the tenant</li>
                <li>Confirm test user can sign in and that groups + group members can be read</li>
                <li>Return Tenant ID, Client ID, Client Secret, and allowed domains to platform admin</li>
              </ul>
            </div>

            <div id="sso-preprovision-section" className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-green-900">👤 Pre-provisioned SSO Users</h3>
                <p className="text-xs text-green-800 mt-1">
                  Users created here are staged and waiting for their first Microsoft sign-in. They will land with the assigned roles immediately — no blank low-access accounts.
                </p>
              </div>

              {/* Staged users list */}
              {(() => {
                const stagedList = users.filter(u => u.AuthType === 'sso' && !u.LastLoginAt);
                if (stagedList.length === 0) {
                  return (
                    <div className="mb-4 p-3 bg-white border border-green-100 rounded text-xs text-gray-500 italic">
                      No staged SSO users yet. Use the form below to pre-create the first company admin.
                    </div>
                  );
                }
                return (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-green-800 mb-2">Awaiting first sign-in ({stagedList.length})</div>
                    <div className="space-y-2">
                      {stagedList.map(u => (
                        <div key={u.UserID} className="flex items-center justify-between bg-white border border-green-100 rounded px-3 py-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{u.FirstName || u.LastName ? `${u.FirstName || ''} ${u.LastName || ''}`.trim() : u.Username}</div>
                            <div className="text-xs text-gray-500">{u.Email} &bull; {u.RoleNames || 'No roles'}</div>
                          </div>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 font-medium">Awaiting login</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="border-t border-green-200 pt-4 mt-2">
                <h4 className="text-xs font-semibold text-green-900 mb-3">+ Add another pre-provisioned user</h4>

              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={stagedSsoUser.firstName}
                      onChange={handleStagedSsoInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="Jane"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={stagedSsoUser.lastName}
                      onChange={handleStagedSsoInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Work Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={stagedSsoUser.email}
                      onChange={handleStagedSsoInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="admin@customer.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Assign Roles *</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {roles.map((role) => (
                      <label key={role.RoleID} className="flex items-start gap-2 bg-white border border-green-100 rounded-lg px-3 py-2 cursor-pointer hover:border-green-300">
                        <input
                          type="checkbox"
                          checked={stagedSsoUser.roleIds.includes(role.RoleID)}
                          onChange={() => handleToggleStagedSsoRole(role.RoleID)}
                          className="mt-0.5 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{role.RoleName}</div>
                          <div className="text-xs text-gray-500">{role.Description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended for the first company admin: <strong>Admin</strong> + <strong>User</strong>.
                  </p>
                </div>

                <div className="bg-white border border-green-100 rounded p-3 text-xs text-gray-700">
                  <p><strong>What happens next:</strong> The user record is created now, linked to this company now, and the selected roles are active now. On first Microsoft sign-in, SpocoDoc binds the Azure AD identity to this staged user instead of creating a blank low-access account.</p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleCreateStagedSsoUser}
                    disabled={saving}
                    className="btn btn-primary"
                  >
                    {saving ? 'Creating...' : 'Pre-create SSO User'}
                  </button>
                </div>
              </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Azure AD Tenant ID *
              </label>
              <input
                type="text"
                name="azureAdTenantId"
                value={ssoData.azureAdTenantId}
                onChange={handleSsoInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in Azure Portal → Azure Active Directory → Properties → Directory ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Azure AD Client ID (Application ID) *
              </label>
              <input
                type="text"
                name="azureAdClientId"
                value={ssoData.azureAdClientId}
                onChange={handleSsoInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in Azure Portal → App registrations → Your app → Application (client) ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Azure AD Client Secret
              </label>
              {ssoData.azureAdTenantId && !ssoData.azureAdClientSecret ? (
                <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-green-700 font-medium">✓ Secret configured (not shown for security)</span>
                </div>
              ) : null}
              <input
                type="password"
                name="azureAdClientSecret"
                value={ssoData.azureAdClientSecret}
                onChange={handleSsoInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder={ssoData.azureAdTenantId ? "Leave blank to keep existing • Enter new value to update" : "Enter client secret"}
              />
              <p className="text-xs text-gray-500 mt-1">
                Create in Azure Portal → App registrations → Your app → Certificates & secrets
                {ssoData.azureAdTenantId && <span className="text-orange-600 font-medium"> • Secret is never stored in database for security</span>}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">📧 Allowed Email Domains</h3>
              <p className="text-xs text-gray-600 mb-3">
                Users with these email domains will automatically be routed to this company's Azure AD tenant. 
                Required for automatic tenant detection during login.
              </p>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDomain())}
                  placeholder="company.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddDomain}
                  className="btn btn-primary btn-sm"
                >
                  Add Domain
                </button>
              </div>

              {ssoData.allowedDomains.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {ssoData.allowedDomains.map((domain) => (
                    <div key={domain} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                      <span className="text-sm font-mono text-gray-700">@{domain}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDomain(domain)}
                        className="text-red-600 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No domains configured. Users won't be able to login via SSO until at least one domain is added.</p>
              )}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="text-sm font-medium text-gray-900 mb-2">🔒 Security & Access Control</h3>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="autoProvisionEnabled"
                  checked={ssoData.autoProvisionEnabled}
                  onChange={handleSsoInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Enable auto-provisioning
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Automatically create user accounts on first login via Azure AD
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="requireAdminApproval"
                  checked={ssoData.requireAdminApproval}
                  onChange={handleSsoInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Require admin approval for new users
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled: New users get status "Pending Access" and cannot login until admin approves and assigns departments/roles
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When disabled: New users get status "Active" with default "User" role and can login immediately (admin assigns departments later)
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={ssoData.isActive}
                  onChange={handleSsoInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-700">
                    SSO is active
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Users can sign in via Azure AD (disable to temporarily block SSO login)
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-yellow-900 mb-2">👥 Default Permissions for Auto-Provisioned Users</h3>
              <ul className="text-xs text-yellow-800 space-y-1 ml-4 list-disc">
                <li><strong>Default Role:</strong> "User" (minimum privileges)</li>
                <li><strong>Department Assignment:</strong> None - admin must assign departments in User Management</li>
                <li><strong>Access Level:</strong> Can view their own data, submit requests</li>
                <li><strong>Upgrades:</strong> Admin can promote to Manager, Finance Admin, or Admin roles</li>
                <li><strong>Multi-Department:</strong> Users can be assigned to multiple departments for cross-functional work</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/companies')}
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save SSO Settings'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'users' && (
          <div className="p-6">
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="w-full sm:w-48">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="w-full sm:w-64">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Roles</option>
                  {roles.map((role) => (
                    <option key={role.RoleID} value={role.RoleName}>
                      {role.RoleName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-2">
                  {users.length === 0 
                    ? 'No users found for this company'
                    : 'No users match your filters'
                  }
                </p>
                <p className="text-sm text-gray-400">
                  {company.SsoConfigured && users.length === 0
                    ? 'Users will be auto-provisioned when they sign in via Azure AD'
                    : users.length === 0 
                      ? 'Configure Azure AD SSO to enable user auto-provisioning'
                      : 'Try adjusting your search or filter'
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableTh field="FullName" sortBy={userSortBy} sortDir={userSortDir} onSort={handleUserSort}>Name</SortableTh>
                      <SortableTh field="Email" sortBy={userSortBy} sortDir={userSortDir} onSort={handleUserSort}>Email</SortableTh>
                      <SortableTh field="RoleNames" sortBy={userSortBy} sortDir={userSortDir} onSort={handleUserSort}>Role</SortableTh>
                      <SortableTh field="IsActive" sortBy={userSortBy} sortDir={userSortDir} onSort={handleUserSort}>Status</SortableTh>
                      <SortableTh field="CreatedAt" sortBy={userSortBy} sortDir={userSortDir} onSort={handleUserSort}>Created</SortableTh>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pagedUsers.map((user) => (
                      <tr key={user.UserID} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.FullName || user.Username}</div>
                          {user.AuthType === 'sso' && (
                            <div className="text-xs text-blue-600">SSO User</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.Email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleChangeUserRole(user)}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                          >
                            {user.RoleNames || 'No Roles'}
                          </button>
                          {user.RoleNames && user.RoleNames.includes(',') && (
                            <div className="text-xs text-gray-500 mt-1">
                              ({user.RoleNames.split(', ').length} roles)
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.IsActive === true || user.IsActive === 1
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.IsActive === true || user.IsActive === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.CreatedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleToggleUserStatus(user.UserID, user.IsActive === true || user.IsActive === 1)}
                              className={`btn btn-sm ${user.IsActive === true || user.IsActive === 1 ? 'btn-warning' : 'btn-success'}`}
                            >
                              {user.IsActive === true || user.IsActive === 1 ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.UserID, user.Username)}
                              className="btn btn-danger btn-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination {...userPaginationProps(sortedUsers.length)} />
              </div>
            )}
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="p-6">
            {usageLoading ? (
              <div className="text-center py-12 text-gray-500">Loading usage metrics...</div>
            ) : !usageData ? (
              <div className="text-center py-12 text-gray-500">No usage data available</div>
            ) : (
              <div className="space-y-8">
                {/* Summary Cards */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Usage Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-blue-700">{usageData.totals.TotalUsers}</div>
                      <div className="text-sm text-blue-600">Total Users</div>
                      <div className="text-xs text-blue-500 mt-1">{usageData.totals.ActiveUsers} active (30d)</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-700">{usageData.totals.TotalInvoices}</div>
                      <div className="text-sm text-green-600">Total Invoices</div>
                      {usageData.totals.TotalInvoices > 0 && usageData.totals.AIProcessedInvoices === usageData.totals.TotalInvoices ? (
                        <div className="text-xs text-green-600 mt-1 font-medium">✓ All AI processed</div>
                      ) : (
                        <div className="text-xs text-green-500 mt-1">{usageData.totals.AIProcessedInvoices} of {usageData.totals.TotalInvoices} AI processed</div>
                      )}
                    </div>
                    <div className="bg-cyan-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-cyan-700">{usageData.totals.AIProcessedPages || 0}</div>
                      <div className="text-sm text-cyan-600">Pages Recognised</div>
                      <div className="text-xs text-cyan-500 mt-1">billable AI OCR pages</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-purple-700">
                        {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(usageData.totals.TotalInvoiceValue)}
                      </div>
                      <div className="text-sm text-purple-600">Invoice Value</div>
                      <div className="text-xs text-purple-500 mt-1">{usageData.totals.ApprovedInvoices} approved</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-amber-700">{usageData.totals.TotalInvoices}</div>
                      <div className="text-sm text-amber-600">Invoices with PDF</div>
                      <div className="text-xs text-amber-500 mt-1">{usageData.storage.InvoicesWithDocs} with attachments</div>
                    </div>
                  </div>
                </div>

                {/* Plan & Pricing (agreed per-customer) */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Plan &amp; Pricing</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Agreed monthly price per enabled module — used for invoicing this customer. Edit in the <span className="font-medium">✨ Modules</span> tab.
                  </p>
                  {(() => {
                    const enabledCodes = companyModules == null
                      ? Object.keys(MODULES_BY_CODE) // legacy: all modules
                      : companyModules;
                    const allCodes = Object.keys(MODULES_BY_CODE);
                    const priceFor = (code) => {
                      if (companyModulePrices && companyModulePrices[code] != null) return companyModulePrices[code];
                      return MODULES_BY_CODE[code]?.priceMonthly || 0;
                    };
                    const total = enabledCodes.reduce((s, c) => s + (priceFor(c) || 0), 0);
                    return (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase text-xs">Module</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase text-xs">Status</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase text-xs">RRP</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase text-xs">Agreed / month</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {allCodes.map(code => {
                              const mod = MODULES_BY_CODE[code];
                              const on = enabledCodes.includes(code) || mod.alwaysOn;
                              const agreed = priceFor(code);
                              const hasOverride = companyModulePriceOverrides && companyModulePriceOverrides[code] != null;
                              return (
                                <tr key={code} className={on ? '' : 'opacity-60'}>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-lg">{mod.icon}</span>
                                      <div>
                                        <div className="font-medium text-gray-900">{mod.name}</div>
                                        <div className="text-xs text-gray-500">{mod.description}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2">
                                    {mod.alwaysOn ? (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">Always on</span>
                                    ) : mod.comingSoon ? (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">Coming soon</span>
                                    ) : on ? (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Enabled</span>
                                    ) : (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Disabled</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-right text-gray-500">
                                    {formatPrice(mod.priceMonthly)}
                                  </td>
                                  <td className="px-4 py-2 text-right font-semibold text-gray-900">
                                    {formatPrice(agreed)}
                                    {hasOverride && (
                                      <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700">custom</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-blue-50">
                            <tr>
                              <td className="px-4 py-3 font-semibold text-blue-900">Agreed total</td>
                              <td className="px-4 py-3 text-xs text-blue-700">{enabledCodes.length} module{enabledCodes.length === 1 ? '' : 's'} enabled</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-right text-lg font-bold text-blue-900">{formatPrice(total)}<span className="text-xs font-normal text-blue-700"> / mo</span></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()}
                </div>

                {/* Invoice Status Breakdown */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Breakdown</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                      <div><span className="font-semibold">{usageData.totals.DraftInvoices}</span> <span className="text-sm text-gray-500">Draft</span></div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div><span className="font-semibold">{usageData.totals.PendingInvoices}</span> <span className="text-sm text-gray-500">Pending</span></div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      <div><span className="font-semibold">{usageData.totals.ApprovedInvoices}</span> <span className="text-sm text-gray-500">Approved</span></div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div><span className="font-semibold">{usageData.totals.RejectedInvoices}</span> <span className="text-sm text-gray-500">Rejected</span></div>
                    </div>
                  </div>
                </div>

                {/* Module/Feature Usage */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Module Usage</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`p-4 rounded-lg border-2 ${
                      usageData.featureUsage.invoiceProcessing ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">📄 Invoice Processing</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          usageData.featureUsage.invoiceProcessing ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {usageData.featureUsage.invoiceProcessing ? 'Active' : 'Not Used'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Invoices: {usageData.totals.TotalInvoices}</div>
                        <div>AI/OCR Processed: {usageData.totals.AIProcessedInvoices}</div>
                        <div>Vendors: {usageData.totals.TotalVendors}</div>
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg border-2 ${
                      usageData.featureUsage.budgeting ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">💰 Budgeting & Analytics</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          usageData.featureUsage.budgeting ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {usageData.featureUsage.budgeting ? 'Active' : 'Not Used'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Budgets: {usageData.totals.TotalBudgets} ({usageData.totals.ActiveBudgets} active)</div>
                        <div>GL Accounts: {usageData.totals.TotalGLAccounts}</div>
                        <div>Departments: {usageData.totals.TotalDepartments}</div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border-2 border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">🔗 Integration Hub</span>
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-gray-200 text-gray-600">
                          Coming Soon
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 space-y-1">
                        <div>API Calls: —</div>
                        <div>ERP Sync: —</div>
                        <div>Webhooks: —</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly Trends */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Invoice Volume (Last 6 Months)</h3>
                  {usageData.monthlyInvoices.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pages</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">AI Processed</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Value</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Volume</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {usageData.monthlyInvoices.map((m) => {
                            const maxCount = Math.max(...usageData.monthlyInvoices.map(x => x.InvoiceCount), 1);
                            const pct = (m.InvoiceCount / maxCount) * 100;
                            return (
                              <tr key={m.Month} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.Month}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-700">{m.InvoiceCount}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-700">{m.TotalPages || 0}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-700">{m.AIProcessedCount}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-700">
                                  {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(m.TotalValue)}
                                </td>
                                <td className="px-4 py-3 w-48">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${pct}%` }}></div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 py-4">No invoice data in the last 6 months</div>
                  )}
                </div>

                {/* Monthly Active Users */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Activity</h3>
                  {usageData.monthlyActivity.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Active Users</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Actions</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity Level</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {usageData.monthlyActivity.map((m) => {
                            const maxActions = Math.max(...usageData.monthlyActivity.map(x => x.TotalActions), 1);
                            const pct = (m.TotalActions / maxActions) * 100;
                            return (
                              <tr key={m.Month} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.Month}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-700">{m.ActiveUsers}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-700">{m.TotalActions}</td>
                                <td className="px-4 py-3 w-48">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pct}%` }}></div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 py-4">No activity data in the last 6 months</div>
                  )}
                </div>

                {/* Top Users */}
                {usageData.topUsers.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Users (Last 30 Days)</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {usageData.topUsers.map((u, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{u.FullName}</div>
                                <div className="text-xs text-gray-500">{u.Email}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">{u.ActionCount}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {new Date(u.LastActivity).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Billing Estimate */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 Estimated Module Assignment</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="font-medium text-gray-700">Core: Invoice Processing</div>
                      <div className="text-gray-500">{usageData.totals.TotalUsers} users, {usageData.totals.TotalInvoices} invoices</div>
                      <div className="text-gray-500">{usageData.totals.AIProcessedInvoices} OCR pages</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">Pro: Budgeting & Analytics</div>
                      <div className="text-gray-500">{usageData.totals.TotalBudgets} budgets, {usageData.totals.TotalGLAccounts} GL accounts</div>
                      <div className={usageData.featureUsage.budgeting ? 'text-green-600 font-medium' : 'text-gray-400'}>
                        {usageData.featureUsage.budgeting ? '✓ In Use' : '○ Not Used'}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">Enterprise: Integration Hub</div>
                      <div className="text-gray-500">API calls, ERP sync, webhooks</div>
                      <div className="text-gray-400">○ Coming Soon</div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button onClick={loadUsageMetrics} className="btn btn-secondary">
                    ↻ Refresh Metrics
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'permissions' && (
          <CompanyPermissionsTab
            targetCompanyId={id}
            companyName={company?.CompanyName}
          />
        )}

        {activeTab === 'modules' && (
          <CompanyModulesTab
            targetCompanyId={id}
            companyName={company?.CompanyName}
          />
        )}

        {activeTab === 'activity' && (
          <CompanyActivityTab
            targetCompanyId={id}
            companyName={company?.CompanyName}
          />
        )}
      </div>

      {/* Role Assignment Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Assign Roles for {selectedUser.FullName || selectedUser.Username}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Current roles: <span className="font-medium">{selectedUser.RoleNames || 'No Roles'}</span>
            </p>
            <p className="text-sm text-blue-600 mb-4">
              ✨ Users can have multiple roles. Permissions from all roles are combined.
            </p>
            <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
              {roles.map((role) => {
                const isSelected = selectedRoleIds.includes(role.RoleID);
                return (
                  <label
                    key={role.RoleID}
                    className={`flex items-start gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleRole(role.RoleID)}
                      className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{role.RoleName}</div>
                      <div className="text-sm text-gray-500">{role.Description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowRoleModal(false); setSelectedUser(null); setSelectedRoleIds([]); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUserRole}
                disabled={selectedRoleIds.length === 0}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save ({selectedRoleIds.length} role{selectedRoleIds.length !== 1 ? 's' : ''})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
